import { useCallback, useEffect, useRef } from 'react'
import { useIdleSession } from './useIdle'

const useScheduledCatalog = (callback) => {
  const isUserActive = useRef(false)
  const delayNextExecution = useRef(false)
  const scheduledTime = useRef(null)

  // Função chamada quando o usuário fica inativo
  const onUserInactive = useCallback(() => {
    isUserActive.current = false
  }, [])

  // Usa o hook para rastrear se houve atividade do usuário no último minuto
  const { reset } = useIdleSession({
    timeout: 1 * 60 * 1000,
    onExpire: onUserInactive,
  })

  // Função que agenda o callback, com verificações contínuas para adiar se necessário
  const scheduleCallback = useCallback(
    (time) => {
      scheduledTime.current = time // Armazena o horário agendado
      delayNextExecution.current = true // Marca que o próximo callback foi adiado

      const interval = setInterval(() => {
        const now = new Date()
        const minutes = now.getMinutes()
        const seconds = now.getSeconds()

        // Verifica se é um minuto antes do horário agendado
        const isOneMinuteBeforeScheduled =
          minutes === (scheduledTime.current.getMinutes() - 1) % 60 &&
          seconds === 0

        if (isOneMinuteBeforeScheduled && isUserActive.current) {
          scheduledTime.current.setMinutes(
            scheduledTime.current.getMinutes() + 5
          ) // Adia em 5 minutos
        } else if (now >= scheduledTime.current && !isUserActive.current) {
          // Se o horário agendado for atingido e o usuário estiver inativo, executa o callback
          callback(now)
          clearInterval(interval)
          delayNextExecution.current = false
          scheduledTime.current = null
        }
      }, 1000)

      return () => clearInterval(interval)
    },
    [callback]
  )

  useEffect(() => {
    // Função que verifica se o horário atual é um dos horários agendados
    const checkTimeAndExecute = () => {
      const now = new Date()
      const minutes = now.getMinutes()
      const seconds = now.getSeconds()

      // Horários agendados para atualização, de 30 em 30 minutos fechados
      const isScheduledTime = (minutes === 0 || minutes === 30) && seconds === 0

      if (isScheduledTime && !delayNextExecution.current) {
        if (isUserActive.current) {
          // Se houver atividade, agenda o callback para 5 minutos depois
          scheduleCallback(new Date(now.getTime() + 5 * 60 * 1000)) // Adia em 5 minutos
        } else {
          callback(now)
        }
      }
    }

    const interval = setInterval(checkTimeAndExecute, 1000)

    return () => clearInterval(interval)
  }, [callback, scheduleCallback])

  useEffect(() => {
    const handleUserActivity = () => {
      isUserActive.current = true
      reset()
    }

    const eventListeners = ['mousemove', 'mousedown', 'keydown', 'touchstart']

    eventListeners.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true })
    })

    return () => {
      eventListeners.forEach((event) => {
        document.removeEventListener(event, handleUserActivity)
      })
    }
  }, [reset])
}

export default useScheduledCatalog
